import {
  UseMutationOptions,
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { Client } from "@/interfaces/domain/Client";
import { Product } from "@/interfaces/domain/Product";
import { Store } from "@/interfaces/domain/Store";
import {
  addClientFavoriteProductRequest,
  getClientFavoriteProductListRequest,
  removeClientFavoriteProductRequest,
} from "@/services/api/client";
import { logger } from "@/services/logger";
import { formatProductToOrderItem } from "@/utils/helpers";

import { useAppSelector } from "../application/store";

const CLIENT_FAVORITES_QUERY_KEY = (storeId) => `favorite-products/${storeId}`;

export const useClientFavoriteProducts = (
  storeId: string,
  options?: UseQueryOptions<Product[]>
) => {
  const { user } = useAppSelector((store) => store.auth);

  const { data, isLoading, refetch } = useQuery<Product[]>({
    queryKey: [CLIENT_FAVORITES_QUERY_KEY(storeId)],
    queryFn: async () => {
      const data = await getClientFavoriteProductListRequest(user?.cliente?._id);
      return data?.filter((product) => (product.estabelecimento as Store)?._id === storeId);
    },
    ...options,
    onError: (error) => {
      logger.error(error);
      options?.onError(error);
    },
  });

  const checkFavoriteByProductId = (productId: string) => {
    return data?.some((p) => p._id === productId);
  };

  return {
    data: data?.map(formatProductToOrderItem),
    checkFavoriteByProductId,
    isLoading,
    refetch,
  };
};

export const useAddFavorite = (
  storeId: string,
  options?: UseMutationOptions<Client, unknown, string>
) => {
  const { user } = useAppSelector((store) => store.auth);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) =>
      addClientFavoriteProductRequest(user?.cliente?._id, productId),
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.refetchQueries([CLIENT_FAVORITES_QUERY_KEY(storeId)]);
      if (options?.onSuccess) {
        options?.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      logger.error(error);
      options?.onError(error, variables, context);
    },
  });
};

export const useRemoveFavorite = (
  storeId: string,
  options?: UseMutationOptions<Client, unknown, string>
) => {
  const { user } = useAppSelector((store) => store.auth);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (productId: string) =>
      removeClientFavoriteProductRequest(user?.cliente?._id, productId),
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.refetchQueries([CLIENT_FAVORITES_QUERY_KEY(storeId)]);
      options?.onSuccess(data, variables, context);
    },
    onError: (error, variables, context) => {
      logger.error(error);
      options?.onError(error, variables, context);
    },
  });
};
